import React, { useState, useEffect, useRef } from 'react';
import { catalogApi, exportApi, furnitureTypesApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { usePreferences } from '../contexts/PreferencesContext';
import { 
  Plus, Search, Package, Pencil, Trash2, X, Upload, Image as ImageIcon,
  Filter, Grid, List, ChevronDown, Euro, Building2, Download, Camera
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const CatalogPage = () => {
  const { isAdmin, isGestionnaire } = useAuth();
  const { formatCurrency, getCurrencySymbol } = usePreferences();
  const canEdit = isAdmin() || isGestionnaire();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterBrand, setFilterBrand] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);
  
  // Available filters
  const [brands, setBrands] = useState([]);
  const [typeCounts, setTypeCounts] = useState([]);
  
  // Furniture types (dynamic from API)
  const [furnitureTypes, setFurnitureTypes] = useState([]);
  const [typeLabels, setTypeLabels] = useState({});

  // Dialog states
  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  // Form data
  const [formData, setFormData] = useState({
    type_mobilier: 'bureau',
    marque: '',
    modele: '',
    fournisseur: '',
    cout_unitaire: '',
    description: '',
    image_url: ''
  });

  // Image upload
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadData();
    loadFurnitureTypes();
  }, []);

  const loadFurnitureTypes = async () => {
    try {
      const response = await furnitureTypesApi.getAll();
      setFurnitureTypes(response.data);
      // Build labels map from types
      const labels = response.data.reduce((acc, ft) => {
        acc[ft.code] = ft.label;
        return acc;
      }, {});
      setTypeLabels(labels);
    } catch (error) {
      console.error('Erreur chargement types mobilier:', error);
    }
  };

  useEffect(() => {
    loadItems();
  }, [filterType, filterBrand, searchTerm]);

  const loadData = async () => {
    try {
      const [brandsRes, typesRes] = await Promise.all([
        catalogApi.getBrands(),
        catalogApi.getTypes()
      ]);
      setBrands(brandsRes.data);
      setTypeCounts(typesRes.data);
    } catch (error) {
      console.error('Erreur chargement données:', error);
    }
  };

  const loadItems = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterType) params.type_mobilier = filterType;
      if (filterBrand) params.marque = filterBrand;
      if (searchTerm) params.search = searchTerm;
      
      const response = await catalogApi.getAll(params);
      setItems(response.data);
    } catch (error) {
      console.error('Erreur chargement catalogue:', error);
      toast.error('Erreur lors du chargement du catalogue');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      type_mobilier: 'bureau',
      marque: '',
      modele: '',
      fournisseur: '',
      cout_unitaire: '',
      description: '',
      image_url: ''
    });
    setEditingItem(null);
    setFormError('');
    setImageFile(null);
    setImagePreview(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (item) => {
    setFormData({
      type_mobilier: item.type_mobilier,
      marque: item.marque,
      modele: item.modele,
      fournisseur: item.fournisseur || '',
      cout_unitaire: item.cout_unitaire ? String(item.cout_unitaire) : '',
      description: item.description || '',
      image_url: item.image_url || ''
    });
    setEditingItem(item);
    setImagePreview(item.image_url || null);
    setImageFile(null);
    setShowDialog(true);
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'];
    if (!validTypes.includes(file.type) && !file.name.match(/\.(jpg|jpeg|png|gif|webp|heic|heif)$/i)) {
      toast.error('Type de fichier non supporté. Utilisez JPG, PNG, GIF, WebP ou HEIC.');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Le fichier est trop volumineux. Maximum 5 Mo.');
      return;
    }

    setImageFile(file);
    setFormData({ ...formData, image_url: '' }); // Clear URL when file is selected

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const clearImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setFormData({ ...formData, image_url: '' });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);

    try {
      const payload = {
        ...formData,
        cout_unitaire: formData.cout_unitaire ? parseFloat(formData.cout_unitaire) : null
      };

      let itemId;
      if (editingItem) {
        await catalogApi.update(editingItem.id, payload);
        itemId = editingItem.id;
        toast.success('Article mis à jour');
      } else {
        const response = await catalogApi.create(payload);
        itemId = response.data.id;
        toast.success('Article ajouté au catalogue');
      }

      // Upload image if a file was selected
      if (imageFile && itemId) {
        setUploadingImage(true);
        try {
          console.log('Uploading image for item:', itemId);
          console.log('Image file:', imageFile.name, imageFile.type, imageFile.size);
          const uploadResponse = await catalogApi.uploadImage(itemId, imageFile);
          console.log('Upload response:', uploadResponse.data);
          toast.success('Image téléchargée');
        } catch (uploadError) {
          console.error('Erreur upload image:', uploadError);
          console.error('Error response:', uploadError.response?.data);
          console.error('Error status:', uploadError.response?.status);
          toast.error("Erreur lors du téléchargement de l'image: " + (uploadError.response?.data?.detail || uploadError.message));
        }
        setUploadingImage(false);
      }

      setShowDialog(false);
      resetForm();
      loadItems();
      loadData();
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      setFormError(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Supprimer "${item.marque} ${item.modele}" du catalogue ?`)) {
      return;
    }

    try {
      await catalogApi.delete(item.id);
      toast.success('Article supprimé');
      loadItems();
      loadData();
    } catch (error) {
      console.error('Erreur suppression:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleExport = async () => {
    try {
      const response = await exportApi.catalog();
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'export_catalogue.csv';
      link.click();
      toast.success('Export téléchargé');
    } catch (error) {
      toast.error("Erreur lors de l'export");
    }
  };

  const clearFilters = () => {
    setFilterType('');
    setFilterBrand('');
    setSearchTerm('');
  };

  const activeFiltersCount = [filterType, filterBrand, searchTerm].filter(Boolean).length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="catalog-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight">
            Catalogue du mobilier
          </h1>
          <p className="text-slate-500 mt-1">
            {items.length} article{items.length > 1 ? 's' : ''} référencé{items.length > 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExport} data-testid="export-catalog-btn">
            <Download className="w-4 h-4 mr-2" />Export CSV
          </Button>
          {canEdit && (
            <Button onClick={openCreateDialog} className="bg-blue-600 hover:bg-blue-700" data-testid="add-catalog-btn">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter au catalogue
            </Button>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher par marque, modèle..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-catalog"
              />
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className={activeFiltersCount > 0 ? 'border-blue-500 text-blue-600' : ''}
              >
                <Filter className="w-4 h-4 mr-2" />
                Filtres
                {activeFiltersCount > 0 && (
                  <Badge className="ml-2 bg-blue-100 text-blue-700">{activeFiltersCount}</Badge>
                )}
              </Button>
              
              <div className="flex border rounded-md">
                <Button
                  variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                  size="icon"
                  onClick={() => setViewMode('grid')}
                  className="rounded-r-none"
                >
                  <Grid className="w-4 h-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                  size="icon"
                  onClick={() => setViewMode('list')}
                  className="rounded-l-none"
                >
                  <List className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Type de mobilier</Label>
                <Select value={filterType || 'all'} onValueChange={(v) => setFilterType(v === 'all' ? '' : v)}>
                  <SelectTrigger data-testid="filter-type">
                    <SelectValue placeholder="Tous les types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les types</SelectItem>
                    {furnitureTypes.map(ft => (
                      <SelectItem key={ft.code} value={ft.code}>
                        {ft.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Marque</Label>
                <Select value={filterBrand || 'all'} onValueChange={(v) => setFilterBrand(v === 'all' ? '' : v)}>
                  <SelectTrigger data-testid="filter-brand">
                    <SelectValue placeholder="Toutes les marques" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les marques</SelectItem>
                    {brands.map(brand => (
                      <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-end">
                <Button variant="ghost" onClick={clearFilters} className="text-slate-500">
                  <X className="w-4 h-4 mr-2" />
                  Réinitialiser
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Type Stats */}
      {typeCounts.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {typeCounts.map(tc => (
            <Badge
              key={tc.type}
              variant={filterType === tc.type ? 'default' : 'outline'}
              className="cursor-pointer hover:bg-slate-100"
              onClick={() => setFilterType(filterType === tc.type ? '' : tc.type)}
            >
              {typeLabels[tc.type] || tc.type} ({tc.count})
            </Badge>
          ))}
        </div>
      )}

      {/* Catalog Items */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Package className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">
              {searchTerm || filterType || filterBrand ? 'Aucun résultat' : 'Catalogue vide'}
            </h3>
            <p className="text-slate-500 mb-4">
              {searchTerm || filterType || filterBrand
                ? 'Essayez de modifier vos critères de recherche'
                : 'Commencez par ajouter des articles au catalogue'}
            </p>
            {canEdit && !searchTerm && !filterType && !filterBrand && (
              <Button onClick={openCreateDialog} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                Ajouter un article
              </Button>
            )}
          </CardContent>
        </Card>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="catalog-grid">
          {items.map(item => (
            <Card key={item.id} className="overflow-hidden hover:shadow-lg transition-shadow group" data-testid={`catalog-item-${item.id}`}>
              {/* Image */}
              <div className="aspect-square bg-slate-100 relative">
                {item.image_url ? (
                  <img
                    src={item.image_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${item.image_url}` : item.image_url}
                    alt={`${item.marque} ${item.modele}`}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Package className="w-16 h-16 text-slate-300" />
                  </div>
                )}
                {/* Type badge */}
                <Badge className="absolute top-2 left-2 bg-white/90 text-slate-700">
                  {typeLabels[item.type_mobilier] || item.type_mobilier}
                </Badge>
                {/* Edit/Delete buttons */}
                {canEdit && (
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                    <Button size="icon" variant="secondary" className="h-8 w-8" onClick={() => openEditDialog(item)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="destructive" className="h-8 w-8" onClick={() => handleDelete(item)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
              
              <CardContent className="p-4">
                <h3 className="font-semibold text-slate-900 truncate">{item.marque}</h3>
                <p className="text-sm text-slate-600 truncate">{item.modele}</p>
                
                <div className="mt-3 flex items-center justify-between">
                  {item.cout_unitaire ? (
                    <span className="text-lg font-bold text-blue-600">
                      {formatCurrency(item.cout_unitaire)}
                    </span>
                  ) : (
                    <span className="text-sm text-slate-400">Prix non défini</span>
                  )}
                  {item.fournisseur && (
                    <span className="text-xs text-slate-500 truncate max-w-[100px]" title={item.fournisseur}>
                      {item.fournisseur}
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card data-testid="catalog-list">
          <div className="divide-y">
            {items.map(item => (
              <div key={item.id} className="p-4 flex items-center gap-4 hover:bg-slate-50" data-testid={`catalog-item-${item.id}`}>
                {/* Thumbnail */}
                <div className="w-16 h-16 rounded-lg bg-slate-100 flex-shrink-0 overflow-hidden">
                  {item.image_url ? (
                    <img
                      src={item.image_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${item.image_url}` : item.image_url}
                      alt={`${item.marque} ${item.modele}`}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="w-8 h-8 text-slate-300" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-slate-900">{item.marque}</h3>
                    <Badge variant="outline" className="text-xs">
                      {typeLabels[item.type_mobilier] || item.type_mobilier}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-600">{item.modele}</p>
                  {item.fournisseur && (
                    <p className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                      <Building2 className="w-3 h-3" />
                      {item.fournisseur}
                    </p>
                  )}
                </div>

                {/* Price */}
                <div className="text-right flex-shrink-0">
                  {item.cout_unitaire ? (
                    <span className="text-lg font-bold text-blue-600">
                      {formatCurrency(item.cout_unitaire)}
                    </span>
                  ) : (
                    <span className="text-sm text-slate-400">-</span>
                  )}
                </div>

                {/* Actions */}
                {canEdit && (
                  <div className="flex gap-1 flex-shrink-0">
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEditDialog(item)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600 hover:text-rose-700" onClick={() => handleDelete(item)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? 'Modifier l\'article' : 'Ajouter au catalogue'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {formError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="type_mobilier">Type *</Label>
                <Select
                  value={formData.type_mobilier}
                  onValueChange={(v) => setFormData({ ...formData, type_mobilier: v })}
                >
                  <SelectTrigger data-testid="form-type">
                    <SelectValue placeholder="Sélectionner un type" />
                  </SelectTrigger>
                  <SelectContent>
                    {furnitureTypes.map(ft => (
                      <SelectItem key={ft.code} value={ft.code}>
                        {ft.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="marque">Marque *</Label>
                <Input
                  id="marque"
                  value={formData.marque}
                  onChange={(e) => setFormData({ ...formData, marque: e.target.value })}
                  placeholder="Ex: Herman Miller"
                  required
                  data-testid="form-marque"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="modele">Modèle *</Label>
              <Input
                id="modele"
                value={formData.modele}
                onChange={(e) => setFormData({ ...formData, modele: e.target.value })}
                placeholder="Ex: Aeron"
                required
                data-testid="form-modele"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fournisseur">Fournisseur</Label>
                <Input
                  id="fournisseur"
                  value={formData.fournisseur}
                  onChange={(e) => setFormData({ ...formData, fournisseur: e.target.value })}
                  placeholder="Ex: Office Depot"
                  data-testid="form-fournisseur"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="cout_unitaire">Coût unitaire ({getCurrencySymbol()})</Label>
                <Input
                  id="cout_unitaire"
                  type="number"
                  step="0.01"
                  value={formData.cout_unitaire}
                  onChange={(e) => setFormData({ ...formData, cout_unitaire: e.target.value })}
                  placeholder="0.00"
                  data-testid="form-cout"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Image du produit</Label>
              <div className="space-y-3">
                {/* Image preview */}
                {imagePreview && (
                  <div className="relative w-32 h-32 rounded-lg border overflow-hidden group">
                    <img
                      src={imagePreview.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${imagePreview}` : imagePreview}
                      alt="Preview"
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = ''; setImagePreview(null); }}
                    />
                    <button
                      type="button"
                      onClick={clearImage}
                      className="absolute top-1 right-1 bg-black/50 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-4 h-4 text-white" />
                    </button>
                  </div>
                )}
                
                {/* Upload button */}
                <div className="flex gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif,.heic,.heif"
                    onChange={handleImageSelect}
                    className="hidden"
                    id="image-upload"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex-1"
                    data-testid="upload-image-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {imageFile ? 'Changer l\'image' : 'Télécharger une image'}
                  </Button>
                </div>
                
                {/* Or URL input */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-slate-500">ou</span>
                  </div>
                </div>
                
                <Input
                  id="image_url"
                  value={formData.image_url}
                  onChange={(e) => {
                    setFormData({ ...formData, image_url: e.target.value });
                    setImageFile(null);
                    setImagePreview(e.target.value || null);
                  }}
                  placeholder="URL de l'image (https://...)"
                  disabled={!!imageFile}
                  data-testid="form-image-url"
                />
                
                <p className="text-xs text-slate-500">
                  Formats acceptés: JPG, PNG, GIF, WebP, HEIC. Taille max: 5 Mo.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Description de l'article..."
                rows={3}
                data-testid="form-description"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Annuler
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={formLoading || uploadingImage} data-testid="form-submit">
                {formLoading || uploadingImage ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                    {uploadingImage ? 'Upload image...' : 'Enregistrement...'}
                  </>
                ) : (editingItem ? 'Mettre à jour' : 'Ajouter')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CatalogPage;
